import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import loginImage from "../Assets/login.json";

const passwordSchema = z
  .object({
    password: z.string().superRefine((val, ctx) => {
      if (val.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Your password must be at least 8 characters long.",
        });
        return;
      }
      if (val.length > 32) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Your password cannot exceed 32 characters.",
        });
        return;
      }
      if (!/[A-Z]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Your password must contain at least one uppercase letter.",
        });
        return;
      }
      if (!/[a-z]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Your password must contain at least one lowercase letter.",
        });
        return;
      }
      if (!/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Your password must contain at least one number.",
        });
        return;
      }
      if (!/[@$!%#]/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Your password must contain at least one special character (e.g., @, %, $).",
        });
        return;
      }
    }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [disabled, setDisabled] = useState(false);
  

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch("/sqa/api/token_validity/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if ( data.success === false) {
          if (data.flag === 0){setStatus("expired");}
          else {setStatus("invalid");}
        }
        else if (data.success === true) setStatus("valid");

      } catch (error) {
        setStatus("invalid");
      }
    };

    verifyToken();
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });
  const onSubmit = async (data) => {
    if (!window.confirm("Are you sure you want to reset your password?")) return;
    try {
      const response = await fetch("/sqa/api/reset_pwd/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: btoa(data.password) }),
      });
  
      const responseData = await response.json();
  
      if (responseData.success===true) {
        setResetSuccess(true);
      } else {
        toast.error(responseData.message || "Failed to reset password. Try again.");
      }
    } catch (error) {
      toast.error("Failed to reset password. Try again.");
    }
  };

  const handleResendResetLink = async () => {
    setDisabled(true); // Disable the button
    setTimeout(() => setDisabled(false), 5000);

    try {
      const response = await fetch("/sqa/api/reset_pwd_req/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({"xtoken": token})
      });

      const responseData = await response.json();

      if (responseData.success === false) {
        toast.error(responseData.msg);
      } else {
        toast.success("Password reset request sent successfully. Please check your email.");
      }
    } catch (error) {
      toast.error("Failed to send reset link. Please try again.");
    }
  };

return (
  <div className="flex min-h-screen">
    {/* Left Side (Image) */}
    <div className="hidden sm:flex sm:w-1/2 md:w-1/3 lg:w-1/2 h-screen justify-center items-center">
    <div className="w-3/4 h-3/4 flex justify-center items-center">
      <img src={loginImage.base64Image} alt="Password Reset Visual" className="max-w-full max-h-full object-contain" />
      </div>
    </div>

    {/* Right Side (Form / Messages) */}
    <div className="w-full sm:w-1/2 md:w-2/3 lg:w-1/2 h-screen flex flex-col justify-center px-6 py-12 lg:px-8">
      {/* Header */}
      <div className="text-3xl font-bold text-center w-full pb-2">
        {status === "expired"
          ? "Password Reset Link Expired"
          : status === "invalid"
          ? "Invalid Reset Link"
          : resetSuccess
          ? "Password Reset Successful"
          : "Set New Password"}
      </div>
      <div className="border-b-4 border-[#4946ed] w-[20%] rounded-md mx-auto text-center"></div>

      {/* Main Content */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg bg-white bg-opacity-50 rounded-lg ">
        {status === "loading" && <p className="text-center text-gray-700">Loading...</p>}

        {status === "invalid" && (
          <div className="text-center text-600">
            <p className="block text-base font-medium text-black">It seems the reset link you used has been altered or is no longer valid. Please go back to your email and click on the original password reset link to proceed.</p>
          </div>
        )}

        {status === "expired" && (
          <div className="text-center">
            <p className="block text-base font-medium text-black">We're sorry, but the password reset link you tried to use is no longer valid. Simply click the button below, and we'll send you another link</p>

            {/* <button
              onClick={handleResendResetLink}
              className="w-full mt-8 rounded-md bg-[#4946ED] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Resend Reset Link
            </button> */}
          <button
          type="submit"
          onClick={handleResendResetLink}
          disabled={disabled}
          className={`mt-4 flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 shadow-sm ${
            disabled
              ? "cursor-not-allowed flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold text-[#4946ED] shadow-sm hover:bg-gray-100"
              : "bg-[#4946ed] text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          }`}
        >
          {disabled ? "Please wait..." : "Request Password Reset"}
        </button>
          </div>
        )}

        {/* Password Reset Form */}
        {status === "valid" && !resetSuccess && (
          <div className="sm:mx-auto sm:w-full sm:max-w-sm bg-white bg-opacity-50  rounded-lg p-6 ">
          <form className="space-y-6 max-w-sm w-full" onSubmit={handleSubmit(onSubmit)}>
            {/* Create Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                Create Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create Password"
                  {...register("password")}
                  className="block p-2 w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </button>
              </div>
              {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  {...register("confirmPassword")}
                  className="block p-2 w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message}</p>}
            </div>

            {/* Password Instructions (On Hover) */}
            <div className="flex justify-end">
              <div
                className="relative text-black-400 flex items-center cursor-pointer"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div className="flex items-center gap-2">
                  <InfoIcon />
                  <span>Password Instructions</span>
                </div>
                {isHovered && (
                  <div className="absolute left-0 top-8 w-80 p-4 bg-white border rounded shadow-lg text-sm text-gray-700">
                    <ul className="list-disc pl-5 text-black">
                      <li>Your password must be at least 8 characters long.</li>
                      <li>It cannot exceed 32 characters.</li>
                      <li>It must contain at least one uppercase letter.</li>
                      <li>It must contain at least one lowercase letter.</li>
                      <li>It must contain at least one number.</li>
                      <li>It must contain at least one special character (e.g., !, @, #, $).</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Reset Password Button */}
            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#4946ed] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                Reset Password
              </button>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate("/auth/user-info/")}
                className="flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold text-[#4946ED] shadow-sm hover:bg-gray-100"
              >
                Go to Login
              </button>
            </div>
          </form>
          </div>
        )}

        {/* Success Message */}
        {resetSuccess && (
        <div className="flex flex-col items-center">
          <p className="text-green-600 font-semibold text-center">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <div className="mt-4 w-1/2">
            <button
              type="button"
              onClick={() => navigate("/auth/user-info/")}
              className="flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold text-[#4946ED] shadow-sm hover:bg-gray-100"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
        
      </div>
    </div>
  </div>
);
};

export default ResetPassword;